import { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/Button";
import { Modal } from "@/components/ui/Modal";
import { SearchableSelect } from "@/components/ui/SearchableSelect";
import { Spinner } from "@/components/ui/Spinner";
import { useDictionaries, useImportRouteVariant, useRouteSources } from "../hooks";
import type {
  ConductorBundle,
  RouteImportResult,
  RouteImportScope,
  RouteImportSource,
  RouteImportVersion,
} from "../types";
import { extractApiError, fmtDate, fmtMeters } from "../utils";

const DEFAULT_BUNDLE: ConductorBundle = { family: "AsXSn", cross_section: 70, count: 4 };

const SCOPES: { value: RouteImportScope; label: string; hint: string }[] = [
  {
    value: "overhead",
    label: "Tylko linie napowietrzne",
    hint: "Słupy stoją pod liniami napowietrznymi — odcinki kablowe są pomijane.",
  },
  {
    value: "all",
    label: "Cała trasa (wszystkie linie)",
    hint: "Także linie ziemne. Przydatne, gdy rysunek nie ma poprawnie opisanych warstw.",
  },
];

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="section-label">{label}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}

const versionLabel = (v: RouteImportVersion): string =>
  [
    v.label,
    v.is_current ? "bieżąca" : null,
    v.length_m != null ? fmtMeters(v.length_m, 0) : null,
    fmtDate(v.created_at),
  ]
    .filter(Boolean)
    .join(" · ");

/**
 * „Wczytaj trasę z projektu ZE" — nowy wariant obliczeń z rysunku trasy DLC_01.
 *
 * Formularz wybiera projekt ZE → wariant trasy → wersję; reszta pól to decyzje,
 * których projekt ZE nie zna: zakres linii i domyślny przewód nadawany każdemu
 * przęsłu (bez przewodów rysunek nie przeszedłby zapisu). Parametry słupów
 * (typ, konstrukcja, funkcja) NIE pochodzą z trasy — uzupełnia się je w edytorze.
 */
export function RouteImportDialog({
  open,
  projectId,
  onClose,
  onImported,
}: {
  open: boolean;
  projectId: string;
  onClose: () => void;
  /** Wywoływane po udanym imporcie — strona przełącza się na nowy wariant. */
  onImported: (result: RouteImportResult) => void;
}) {
  const sourcesQ = useRouteSources(open);
  const { data: dictionaries } = useDictionaries();
  const importMutation = useImportRouteVariant(projectId);

  const sources = useMemo<RouteImportSource[]>(() => sourcesQ.data ?? [], [sourcesQ.data]);
  const [connectionId, setConnectionId] = useState("");
  const [routeVariantId, setRouteVariantId] = useState("");
  const [versionId, setVersionId] = useState("");
  const [name, setName] = useState("");
  const [scope, setScope] = useState<RouteImportScope>("overhead");
  const [bundle, setBundle] = useState<ConductorBundle>(DEFAULT_BUNDLE);

  const source = sources.find((s) => s.connection_id === connectionId) ?? null;
  const routeVariant = source?.variants.find((v) => v.id === routeVariantId) ?? null;
  const versions = routeVariant?.versions ?? [];

  // Domyślny wybór w dół hierarchii: pierwszy projekt → wariant obowiązujący
  // (albo pierwszy) → wersja bieżąca (albo najnowsza).
  useEffect(() => {
    if (!open || !sources.length) return;
    if (!sources.some((s) => s.connection_id === connectionId)) {
      setConnectionId(sources[0].connection_id);
    }
  }, [open, sources, connectionId]);

  useEffect(() => {
    if (!source) return;
    if (!source.variants.some((v) => v.id === routeVariantId)) {
      const binding = source.variants.find((v) => v.is_binding) ?? source.variants[0];
      setRouteVariantId(binding?.id ?? "");
    }
  }, [source, routeVariantId]);

  useEffect(() => {
    if (!versions.length) {
      setVersionId("");
      return;
    }
    if (!versions.some((v) => v.id === versionId)) {
      setVersionId((versions.find((v) => v.is_current) ?? versions[0]).id);
    }
  }, [versions, versionId]);

  const version = versions.find((v) => v.id === versionId) ?? null;
  const suggestedName = source && version ? `${source.name} — ${version.label}` : "";

  const families = dictionaries?.families ?? [DEFAULT_BUNDLE.family];
  const crossSections = dictionaries?.cross_sections ?? [DEFAULT_BUNDLE.cross_section];

  const submit = () => {
    if (!routeVariantId || !versionId) return;
    importMutation.mutate(
      {
        route_variant: routeVariantId,
        route_version: versionId,
        name: name.trim(),
        line_scope: scope,
        bundle,
      },
      { onSuccess: onImported },
    );
  };

  return (
    <Modal open={open} onClose={onClose} title="Wczytaj trasę z projektu ZE" size="lg">
      {sourcesQ.isLoading ? (
        <div className="flex justify-center py-10">
          <Spinner />
        </div>
      ) : !sources.length ? (
        <p className="py-6 text-center font-mono text-xs text-[var(--color-text-muted)]">
          Żaden projekt ZE nie ma jeszcze wgranej wersji trasy — nie ma czego wczytać.
        </p>
      ) : (
        <div className="space-y-3">
          <Field label="Projekt ZE">
            <SearchableSelect
              value={connectionId}
              onChange={(e) => setConnectionId(e.target.value)}
              placeholder="Wybierz projekt"
              options={sources.map((s) => ({
                value: s.connection_id,
                label: s.client_name ? `${s.name} — ${s.client_name}` : s.name,
              }))}
            />
          </Field>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Wariant trasy">
              <select
                className="input-base"
                value={routeVariantId}
                onChange={(e) => setRouteVariantId(e.target.value)}
              >
                {(source?.variants ?? []).map((v) => (
                  <option key={v.id} value={v.id}>
                    Wariant {v.label}
                    {v.is_binding ? " (obowiązujący)" : ""}
                  </option>
                ))}
              </select>
            </Field>

            <Field label="Wersja trasy">
              <select
                className="input-base"
                value={versionId}
                onChange={(e) => setVersionId(e.target.value)}
              >
                {versions.map((v) => (
                  <option key={v.id} value={v.id}>
                    {versionLabel(v)}
                  </option>
                ))}
              </select>
            </Field>
          </div>

          <Field label="Zakres linii">
            <select
              className="input-base"
              value={scope}
              onChange={(e) => setScope(e.target.value as RouteImportScope)}
            >
              {SCOPES.map((s) => (
                <option key={s.value} value={s.value}>
                  {s.label}
                </option>
              ))}
            </select>
            <p className="mt-1 font-mono text-[10px] text-[var(--color-text-muted)]">
              {SCOPES.find((s) => s.value === scope)?.hint}
            </p>
          </Field>

          <Field label="Domyślny przewód przęseł">
            <div className="flex flex-wrap items-center gap-2">
              <select
                className="input-base w-28"
                value={bundle.family}
                onChange={(e) => setBundle({ ...bundle, family: e.target.value })}
              >
                {families.map((f) => (
                  <option key={f} value={f}>
                    {f}
                  </option>
                ))}
              </select>
              <select
                className="input-base w-24"
                value={bundle.cross_section}
                onChange={(e) =>
                  setBundle({ ...bundle, cross_section: Number(e.target.value) })
                }
              >
                {crossSections.map((w) => (
                  <option key={w} value={w}>
                    {w} mm²
                  </option>
                ))}
              </select>
              <span className="font-mono text-[11px] text-[var(--color-text-secondary)]">×</span>
              <input
                type="number"
                min={1}
                step={1}
                className="input-base w-20 text-right"
                value={bundle.count}
                onChange={(e) =>
                  setBundle({ ...bundle, count: Math.max(1, Number(e.target.value) || 1) })
                }
              />
              <span className="font-mono text-[11px] text-[var(--color-text-secondary)]">żył</span>
            </div>
            <p className="mt-1 font-mono text-[10px] text-[var(--color-text-muted)]">
              Ten sam pęczek trafia na każde wczytane przęsło — projekt ZE nie zna przekrojów.
            </p>
          </Field>

          <Field label="Nazwa wariantu obliczeń">
            <input
              className="input-base"
              placeholder={suggestedName}
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </Field>

          <p className="border border-[var(--color-border)] px-3 py-2 font-mono text-[10px] leading-relaxed text-[var(--color-text-muted)]">
            Wczytujemy geometrię: wierzchołek linii = słup, odcinek między nimi = przęsło.
            Słupy i złącza z rysunku ZE nazywają powstałe punkty (złącze nN staje się
            przyłączem). Parametry obliczeniowe słupów — typ, konstrukcja, funkcja —
            uzupełnij po imporcie w edytorze.
          </p>

          {importMutation.error != null && (
            <p className="font-mono text-xs text-[var(--color-primary)]">
              {extractApiError(importMutation.error)}
            </p>
          )}

          <div className="flex justify-end gap-2 pt-1">
            <Button type="button" variant="ghost" size="sm" onClick={onClose}>
              Anuluj
            </Button>
            <Button
              type="button"
              size="sm"
              disabled={!versionId}
              loading={importMutation.isPending}
              onClick={submit}
            >
              Wczytaj trasę
            </Button>
          </div>
        </div>
      )}
    </Modal>
  );
}
